import React, { useState, useEffect, useRef, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import { Braintree, HostedField } from "react-braintree-fields";
import PropTypes from "prop-types";
import yup from "yup";
import { isInViewport } from "../../utils/dom";
import {
  FormFieldGroup,
  FormFieldWrapper,
  Label,
  InputWrapper,
  brainTreeFormStyles,
  BillingNotice,
  TOS,
  FormSubmit,
  PromoCode,
  ApplyDiscount
} from "./styled/fastrack-payment-form";

function FastrackPaymentForm({
  email,
  promoInputValue,
  isFreePlan,
  onPromoChange,
  onPromoSubmit,
  onFormSubmit
}) {
  const [braintreeReady, setBraintreeReady] = useState(false);
  const [userMovedFocus, setUserMovedFocus] = useState(false);

  const paymentFormValidationSchema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .email("Invalid email")
        .required("Required"),
      tos: yup.boolean().oneOf([true]),
      ...(!isFreePlan && {
        number: yup.boolean().oneOf([true]),
        expirationDate: yup.boolean().oneOf([true]),
        cvv: yup.boolean().oneOf([true])
      })
    });
  }, [isFreePlan]);

  // focus the cc number field if its in viewport and the user hasn't moved focus yet
  useEffect(() => {
    if (braintreeReady && ccNumberRef.current) {
      const ccNumField = document.getElementById(ccNumberRef.current.fieldId);
      const ccNumberInputIsInViewport = isInViewport(ccNumField);

      if (ccNumberInputIsInViewport && !userMovedFocus) {
        ccNumberRef.current.focus();
      }
    }
  }, [braintreeReady]);

  // Refs
  const braintreeRef = useRef(null);
  const ccNumberRef = useRef(null);

  return (
    <Formik
      validateOnMount={true}
      initialValues={{
        email: email || "",
        number: false,
        expirationDate: false,
        cvv: false,
        tos: false
      }}
      validationSchema={paymentFormValidationSchema}
      onSubmit={(formValues, formikBag) => {
        if (isFreePlan) {
          onFormSubmit({ formValues, formikBag, braintreePayload: null });
        } else {
          braintreeRef.current.tokenize().then((braintreePayload) => {
            onFormSubmit({ formValues, formikBag, braintreePayload });
          });
        }
      }}>
      {({ setFieldValue, setFieldTouched, isValid, isSubmitting }) => (
        <Form onFocus={() => setUserMovedFocus(true)}>
          <FormFieldWrapper>
            <Label>Email</Label>
            <InputWrapper>
              <Field name="email" placeholder="you@example.com" type="email" />
            </InputWrapper>
          </FormFieldWrapper>
          {!isFreePlan && (
            <Braintree
              ref={braintreeRef}
              authorization={window.braintree_token}
              onAuthorizationSuccess={() => setBraintreeReady(true)}
              styles={brainTreeFormStyles}>
              <FormFieldWrapper>
                <Label>Card number</Label>
                <InputWrapper>
                  <HostedField
                    type="number"
                    placeholder="Card number"
                    ref={ccNumberRef}
                    onValidityChange={({ isValid, isTouched }) => {
                      setFieldValue("number", isValid, true);
                      setFieldTouched("number", isTouched, true);
                    }}
                  />
                </InputWrapper>
              </FormFieldWrapper>
              <FormFieldGroup>
                <FormFieldWrapper>
                  <Label>Expiration</Label>
                  <HostedField
                    type="expirationDate"
                    placeholder="MM/YYYY"
                    onValidityChange={({ isValid, isTouched }) => {
                      setFieldValue("expirationDate", isValid, true);
                      setFieldTouched("expirationDate", isTouched, true);
                    }}
                  />
                </FormFieldWrapper>
                <FormFieldWrapper>
                  <Label>CVV</Label>
                  <InputWrapper>
                    <HostedField
                      type="cvv"
                      placeholder="CVV"
                      onValidityChange={({ isValid, isTouched }) => {
                        setFieldValue("cvv", isValid, true);
                        setFieldTouched("cvv", isTouched, true);
                      }}
                    />
                  </InputWrapper>
                </FormFieldWrapper>
              </FormFieldGroup>
            </Braintree>
          )}
          <PromoCode>
            <FormFieldWrapper>
              <Label htmlFor="promo-code">Promo code</Label>
              <InputWrapper>
                <input
                  id="promo-code"
                  defaultValue={promoInputValue}
                  onChange={onPromoChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onPromoSubmit();
                  }}
                />
                <ApplyDiscount
                  type="button"
                  disabled={!promoInputValue}
                  onClick={onPromoSubmit}>
                  Apply
                </ApplyDiscount>
              </InputWrapper>
            </FormFieldWrapper>
          </PromoCode>
          <BillingNotice>
            Your credit card <strong>will not be billed</strong> if you cancel
            within trial period.
          </BillingNotice>
          <TOS>
            <Field type="checkbox" name="tos" id="tos-agreement" />
            <label htmlFor="tos-agreement">
              I have read and agree with the{" "}
              <a href="/tos" target="_blank" tabIndex={-1}>
                Terms of Service
              </a>
              ,{" "}
              <a
                href="http://wrstudios.com/privacy_policy"
                target="_blank"
                tabIndex={-1}>
                Privacy Policy
              </a>
              , and{" "}
              <a
                href="http://wrstudios.com/dmca_policy"
                target="_blank"
                tabIndex={-1}>
                DMCA Policy
              </a>
            </label>
          </TOS>
          <FormSubmit type="submit" disabled={!isValid || isSubmitting}>
            {isSubmitting ? "Starting Free Trial..." : "Start Free Trial"}
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
}

FastrackPaymentForm.propTypes = {
  email: PropTypes.string,
  promoInputValue: PropTypes.string,
  isFreePlan: PropTypes.bool.isRequired,
  onPromoChange: PropTypes.func.isRequired,
  onPromoSubmit: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired
};

export default FastrackPaymentForm;
